<script setup lang="ts">
import { computed, provide, ref } from 'vue'
import type { BookPageConfig } from '../configs/types'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import { theme as brandTheme } from '@/config/brands'
import { t } from '@/i18n/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
}

type StorlynBook = GetContentGetcontentdetailResponse['data'] & {
  contentClassName?: string
  contentclassname?: string
  languageId?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
}>()

const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

const tagList = computed(() => (props.book.tagList || []).filter((tag): tag is { id: string; name: string } => !!tag.name))
const categoryName = computed(() => {
  const book = props.book as StorlynBook
  return book.contentClassName || book.contentclassname || ''
})
const chapterCount = computed(() => props.book.chapterNum || props.book.lastChapter?.sequenceNo || 0)
const bookshelfCount = computed(() => Number(props.book.bookshelfCount || 0).toLocaleString())
const viewCount = computed(() => {
  const value = Number(props.book.viewUsers || 0)
  if (value >= 1000000)
    return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000)
    return `${(value / 1000).toFixed(1)}K`
  return String(value)
})
const languageText = computed(() => {
  const languageMap: Record<string, string> = {
    'en': t('English'),
    'zh-TW': '繁體中文',
    'ja': '日本語',
    'ko': '한국어',
    'fr': 'Français',
    'de': 'Deutsch',
    'es': 'Español',
    'id': 'Bahasa Indonesia',
  }
  const languageId = (props.book as StorlynBook).languageId || ''
  return languageMap[languageId] || languageId || t('Language')
})
const pageBackground = computed(() => brandTheme.bgGlobal || '#FFF7FB')
const introExpanded = ref(false)

function toggleIntro() {
  introExpanded.value = !introExpanded.value
}

provide('theme', { background: pageBackground.value })
</script>

<template>
  <div class="storlyn-book-page min-h-screen pb-92px" :style="{ background: pageBackground }">
    <div class="px-16px pt-14px" :class="{ 'mt-60px': bannerShow }">
      <section class="mt-8px flex items-start gap-14px">
        <img
          class="w-102px h-136px rounded-10px object-cover flex-shrink-0 shadow-[0_8px_24px_rgba(0,0,0,0.14)]"
          :src="coverUrl"
          alt="Book Cover"
        >

        <div class="min-w-0 flex-1 pt-4px">
          <h1 class="text-18px lh-26px font-700 text-n-text-1 ">
            {{ book.contentName }}
          </h1>

          <div class="mt-10px flex flex-wrap items-center gap-x-8px gap-y-6px text-13px lh-18px text-n-text-2">
            <span class="inline-flex items-center gap-4px">
              <i-icon-park-outline-world class="text-15px" />
              {{ languageText }}
            </span>
            <span class="text-n-text-3">•</span>
            <span>{{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
          </div>

          <div class="mt-14px flex items-center gap-10px min-w-0">
            <span v-if="categoryName" class="storlyn-category-tag">
              {{ categoryName }}
            </span>
            <span class="text-14px lh-20px text-n-text-2 line-clamp-1">By: {{ book.authorInfo?.author }}</span>
          </div>
        </div>
      </section>

      <section class="storlyn-stats-card mt-18px bg-n-bg-card">
        <div class="storlyn-stat-col">
          <div class="storlyn-stat-number text-n-text-1">
            {{ chapterCount }}
          </div>
          <div class="storlyn-stat-text text-n-primary">
            {{ config.texts.chapters }}
          </div>
        </div>
        <div class="storlyn-stat-col">
          <div class="storlyn-stat-number text-n-text-1">
            {{ bookshelfCount }}
          </div>
          <div class="storlyn-stat-text text-n-primary">
            In Library
          </div>
        </div>
        <div class="storlyn-stat-col">
          <div class="storlyn-stat-number text-n-text-1">
            {{ viewCount }}
          </div>
          <div class="storlyn-stat-text text-n-primary">
            Views
          </div>
        </div>
      </section>

      <section class="storlyn-section mt-18px">
        <h2 class="storlyn-section-title text-n-text-1">
          Synopsis
        </h2>

        <div class="mt-12px">
          <p
            class="text-14px lh-28px text-n-text-2 break-words"
            :class="{ 'line-clamp-6': !introExpanded }"
          >
            {{ book.introduce }}
          </p>
          <button
            v-if="book.introduce && !introExpanded"
            type="button"
            class="storlyn-intro-toggle mt-8px text-13px lh-18px text-n-primary"
            @click="toggleIntro"
          >
            <span>More</span>
            <i-icon-park-outline-down class="text-12px" />
          </button>
          <button
            v-else-if="book.introduce && introExpanded"
            type="button"
            class="storlyn-intro-toggle mt-8px text-13px lh-18px text-n-text-2"
            @click="toggleIntro"
          >
            <span>Show less</span>
            <i-icon-park-outline-up class="text-12px" />
          </button>
        </div>

        <div v-if="tagList.length" class="mt-18px flex flex-nowrap gap-12px overflow-x-auto storlyn-hide-scrollbar">
          <button
            v-for="tag in tagList"
            :key="tag.id"
            type="button"
            class="storlyn-tag text-n-primary bg-n-bg-tag"
            @click="emit('goto-tags', tag.id || '')"
          >
            {{ tag.name }}
          </button>
        </div>
      </section>

      <div class="storlyn-divider" />

      <section class="storlyn-section">
        <h2 class="storlyn-section-title text-n-text-1">
          Content
        </h2>

        <div v-if="introduce" class="mt-12px text-14px lh-24px text-n-primary">
          <p v-html="introduce" />
        </div>

        <h3 class="mt-14px text-18px lh-26px font-700 text-n-text-1">
          {{ book.firstChapter?.chapterName }}
        </h3>

        <div class="mt-12px text-15px lh-30px text-n-text-2">
          <p
            v-for="(item, index) in paragraphs.slice(0, 12)"
            :key="index"
            class="mt-12px first:mt-0"
          >
            {{ item }}
          </p>
        </div>
      </section>

      <div class="h-16px" />
    </div>

    <BizPopupBookTOCFlipnovel
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :style="{ '--toc-border-color': 'rgba(216, 115, 147, 0.14)' }"
    />

    <BasicFixedBottom class="shadow-[0_-4px_18px_rgba(0,0,0,0.06)] !pb-0">
      <div class="storlyn-bottom-bar bg-n-bg-global border-n-border-primary">
        <button class="storlyn-bottom-icon text-n-text-1" type="button" @click="emit('open-toc')">
          <i-icon-park-outline-align-text-left class="text-16px" />
          <span class="mt-2px text-10px lh-11px">Chapters</span>
        </button>
        <button class="storlyn-bottom-read text-white" type="button" @click="emit('read')">
          <i-icon-park-outline-book-open class="text-16px" />
          <span>Read</span>
        </button>
        <div
          class="storlyn-bottom-icon"
          :class="{ 'text-n-text-1': !isInShelf, 'opacity-50': shelfLoading }"
          :style="{ color: isInShelf ? brandTheme.primaryColor : undefined }"
          @click="emit('toggle-shelf')"
        >
          <i-assets-book-added-shelf v-if="isInShelf" class="w-20px h-20px pointer-events-none" />
          <i-assets-book-add-shelf v-else class="w-20px h-20px pointer-events-none" />
          <p class="mt-2px text-10px lh-11px">
            {{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}
          </p>
        </div>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style scoped>
.storlyn-book-page {
  overflow-x: hidden;
}

.storlyn-hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.storlyn-hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.storlyn-category-tag {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 11px;
  line-height: 14px;
  color: #fff;
  background: linear-gradient(90deg, #ff4ca0 0%, #d87393 100%);
}

.storlyn-stats-card {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-radius: 18px;
  padding: 18px 10px;
}

.storlyn-stat-col {
  text-align: center;
}

.storlyn-stat-number {
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
}

.storlyn-stat-text {
  margin-top: 8px;
  font-size: 13px;
  line-height: 18px;
}

.storlyn-section-title {
  position: relative;
  padding-left: 14px;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
}

.storlyn-section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 3px;
  width: 5px;
  height: 20px;
  border-radius: 999px;
  background: var(--un-preset-theme-colors-n-primary-gradient, linear-gradient(180deg, #ff74a9 0%, #f35d96 100%));
}

.storlyn-tag {
  border: 0;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
}

.storlyn-divider {
  height: 1px;
  margin: 22px 0;
  background: var(--un-preset-theme-colors-n-border-primary, rgba(0, 0, 0, 0.08));
}

.storlyn-intro-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  font-weight: 500;
}

.storlyn-bottom-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
}

.storlyn-bottom-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  padding: 2px 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.storlyn-bottom-read {
  flex: 0 0 200px;
  height: 45px;
  border: 0;
  border-radius: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 18px;
  gap: 6px;
  background: linear-gradient(90deg, var(--rr-primary-start, #FF81B0) 0%, var(--rr-primary-strong, #EE3BA4) 100%);
}
</style>
